import { useEffect, useState, useCallback } from 'react';
import { Shield, Plus, Trash2, Eye, Wallet, Users, LayoutDashboard, Trophy, ScrollText, Check, X, LifeBuoy } from 'lucide-react';
import { toast } from 'sonner';
import { api, apiError } from '@/lib/api';
import { SupportRequestsTab } from '@/components/admin/SupportRequestsTab';
import { LoadingState, EmptyState } from '@/components/states';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const STATUSES = ['draft', 'scheduled', 'active', 'closed', 'archived'];
const inp = 'w-full rounded-xl border border-border bg-surface-input px-4 py-2.5 text-white placeholder:text-muted-foreground focus:border-brand/60 focus:outline-none';

function fmt(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return '—'; }
}

export default function Admin() {
  return (
    <div className="space-y-6">
      <h1 className="flex items-center gap-2 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
        <Shield className="h-7 w-7 text-brand" /> Administration
      </h1>
      <Tabs defaultValue="dashboard">
        <TabsList className="flex-wrap bg-surface-card">
          <TabsTrigger value="dashboard" data-testid="admin-tab-dashboard"><LayoutDashboard className="mr-1.5 h-4 w-4" /> Tableau de bord</TabsTrigger>
          <TabsTrigger value="payments" data-testid="admin-tab-payments"><Wallet className="mr-1.5 h-4 w-4" /> Commandes et attribution des crédits</TabsTrigger>
          <TabsTrigger value="support" data-testid="admin-tab-support"><LifeBuoy className="mr-1.5 h-4 w-4" /> Demandes d'assistance crédits</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="mr-1.5 h-4 w-4" /> Utilisateurs</TabsTrigger>
          <TabsTrigger value="challenges" data-testid="admin-tab-challenges"><Trophy className="mr-1.5 h-4 w-4" /> Challenges</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit"><ScrollText className="mr-1.5 h-4 w-4" /> Journal</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard" className="mt-5"><Dashboard /></TabsContent>
        <TabsContent value="payments" className="mt-5"><Payments /></TabsContent>
        <TabsContent value="support" className="mt-5"><SupportRequestsTab /></TabsContent>
        <TabsContent value="users" className="mt-5"><UsersTab /></TabsContent>
        <TabsContent value="challenges" className="mt-5"><Challenges /></TabsContent>
        <TabsContent value="audit" className="mt-5"><Audit /></TabsContent>
      </Tabs>
    </div>
  );
}

function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get('/admin/stats').then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return <LoadingState />;
  const cards = [
    ['Utilisateurs', s.users], ['Premium', s.premium_users], ['Morceaux', s.songs],
    ['Participations', s.active_entries], ['Paiements en attente', s.pending_payments],
    ['Paiements crédités', s.credited_payments], ['Revenu (FCFA)', s.revenue_fcfa],
  ];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="admin-dashboard">
      {cards.map(([label, val]) => (
        <div key={label} className="card-surface p-5">
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="mt-1 font-display text-3xl font-extrabold text-brand">{val}</p>
        </div>
      ))}
    </div>
  );
}

const PAY_STATUS = { PENDING_VERIFICATION: 'text-[#F59E0B]', CREDITED: 'text-[#22C55E]', REJECTED: 'text-destructive', CREATED: 'text-muted-foreground', CONFIRMED: 'text-[#22C55E]' };

function Payments() {
  const [filter, setFilter] = useState('PENDING_VERIFICATION');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const q = filter === 'ALL' ? '' : `?status=${filter}`;
    try { const { data } = await api.get(`/admin/payments${q}`); setRows(data); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const validate = async (id) => {
    const ref = (window.prompt('Référence Wave vérifiée (facultatif — laisser vide pour valider sans) :') || '').trim();
    try {
      const { data } = await api.post(`/admin/payments/${id}/validate`, { verified_reference: ref || null });
      toast.success(`Paiement validé (+${data?.credits ?? ''} crédits)`); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const reject = async (id) => {
    const reason = window.prompt('Motif du rejet ?') || '';
    try { await api.post(`/admin/payments/${id}/reject`, { reason }); toast.success('Paiement rejeté'); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-payments">
      <div className="flex flex-wrap gap-2">
        {['PENDING_VERIFICATION', 'CREDITED', 'REJECTED', 'ALL'].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`payments-filter-${f}`}
                  className={`rounded-full px-4 py-1.5 text-sm font-semibold ${filter === f ? 'bg-brand text-black' : 'bg-surface-card text-white hover:bg-surface-cardHover'}`}>
            {f === 'PENDING_VERIFICATION' ? 'En attente' : f === 'CREDITED' ? 'Crédités' : f === 'REJECTED' ? 'Rejetés' : 'Tous'}
          </button>
        ))}
      </div>
      {loading ? <LoadingState /> : rows.length === 0 ? (
        <EmptyState icon={Wallet} title="Aucun paiement" description="Aucune commande pour ce filtre." />
      ) : (
        <div className="space-y-3">
          {rows.map((o) => (
            <div key={o.id} className="card-surface p-4" data-testid={`admin-payment-${o.reference}`}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="font-mono text-sm text-brand">{o.reference}</p>
                  <p className="text-sm text-white">{o.user?.display_name || o.user_email} · {o.amount_fcfa} FCFA · {o.credits} crédits</p>
                  <p className="mt-1 font-mono text-xs text-muted-foreground">
                    Wave: {o.wave_payer_number || '—'} · Tx: {o.provider_transaction_id || '—'}{o.client_reference ? ` · Réf. vérifiée: ${o.client_reference}` : ''} · Payé: {fmt(o.declared_paid_at)} · Soumis: {fmt(o.submitted_at)}
                  </p>
                  <p className={`mt-1 text-xs font-semibold ${PAY_STATUS[o.status] || ''}`}>{o.status}{o.reject_reason ? ` — ${o.reject_reason}` : ''}</p>
                </div>
                {o.status === 'PENDING_VERIFICATION' && (
                  <div className="flex flex-shrink-0 gap-2">
                    <button onClick={() => validate(o.id)} data-testid={`validate-${o.reference}`}
                            className="flex items-center gap-1 rounded-full bg-[#22C55E] px-4 py-2 text-sm font-semibold text-black">
                      <Check className="h-4 w-4" /> Valider
                    </button>
                    <button onClick={() => reject(o.id)} data-testid={`reject-${o.reference}`}
                            className="flex items-center gap-1 rounded-full bg-surface-card px-4 py-2 text-sm font-semibold text-destructive hover:bg-white/5">
                      <X className="h-4 w-4" /> Rejeter
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UsersTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/users').then(({ data }) => setRows(data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const adjust = async (u) => {
    const raw = window.prompt(`Ajuster les crédits de ${u.display_name} (ex: 25 ou -5) :`);
    if (!raw) return;
    const amount = parseInt(raw, 10);
    if (Number.isNaN(amount) || amount === 0) { toast.error('Montant invalide'); return; }
    try { await api.post(`/admin/users/${u.id}/adjust`, { amount, reason: 'Ajustement admin' }); toast.success('Crédits ajustés'); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <LoadingState />;
  return (
    <div className="space-y-2" data-testid="admin-users">
      {rows.map((u) => (
        <div key={u.id} className="card-surface flex items-center justify-between p-3">
          <div className="min-w-0">
            <p className="truncate font-semibold text-white">{u.display_name} <span className="text-xs text-muted-foreground">@{u.username}</span></p>
            <p className="text-xs text-muted-foreground">{u.role} · {u.is_premium ? 'Premium' : 'Gratuit'}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-display font-bold text-brand">{u.credits_balance} cr.</span>
            <button onClick={() => adjust(u)} data-testid={`adjust-${u.id}`}
                    className="rounded-full bg-surface-card px-3 py-1.5 text-xs font-semibold text-white hover:bg-surface-cardHover">Ajuster</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Audit() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get('/admin/audit').then(({ data }) => setRows(data)).finally(() => setLoading(false)); }, []);
  if (loading) return <LoadingState />;
  if (rows.length === 0) return <EmptyState icon={ScrollText} title="Journal vide" description="Aucune action administrative enregistrée." />;
  return (
    <div className="space-y-2" data-testid="admin-audit">
      {rows.map((r) => (
        <div key={r.id} className="card-surface flex items-center justify-between p-3 text-sm">
          <div><span className="font-semibold text-white">{r.action}</span> <span className="text-muted-foreground">— {r.target_type} {r.target_id?.slice(0, 8)}</span></div>
          <div className="text-right text-xs text-muted-foreground">{r.admin_name || '—'}<br />{fmt(r.created_at)}</div>
        </div>
      ))}
    </div>
  );
}

// ---- Existing challenge management (unchanged behavior) ----
function Challenges() {
  const [challenges, setChallenges] = useState([]);
  const [styles, setStyles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entriesFor, setEntriesFor] = useState(null);
  const [entries, setEntries] = useState([]);

  const load = () => {
    setLoading(true);
    api.get('/admin/challenges').then(({ data }) => setChallenges(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); api.get('/styles').then(({ data }) => setStyles(data)); }, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/challenges/${id}/status`, { status }); toast.success('Statut mis à jour'); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const viewEntries = async (c) => {
    setEntriesFor(c);
    const { data } = await api.get(`/admin/challenges/${c.id}/entries`);
    setEntries(data);
  };
  const removeEntry = async (id) => {
    try { await api.delete(`/admin/entries/${id}`); toast.success('Participation retirée'); setEntries((p) => p.filter((e) => e.id !== id)); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <LoadingState />;
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><ChallengeForm styles={styles} onSaved={load} /></div>
      {challenges.length === 0 ? (
        <EmptyState icon={Shield} title="Aucun challenge" description="Crée ton premier challenge." />
      ) : (
        <div className="space-y-3">
          {challenges.map((c) => (
            <div key={c.id} className="card-surface flex flex-col gap-3 p-4 sm:flex-row sm:items-center" data-testid={`admin-challenge-${c.slug}`}>
              <div className="flex-1">
                <p className="font-display font-semibold text-white">{c.emoji} {c.title}</p>
                <p className="text-sm text-muted-foreground">{c.entries_count} participations</p>
              </div>
              <Select value={c.status} onValueChange={(v) => setStatus(c.id, v)}>
                <SelectTrigger className="w-40 border-border bg-surface-input" data-testid={`admin-status-${c.slug}`}><SelectValue /></SelectTrigger>
                <SelectContent className="glass">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <button onClick={() => viewEntries(c)} className="flex items-center gap-1.5 rounded-full bg-surface-cardHover px-4 py-2 text-sm text-white hover:bg-white/10" data-testid={`admin-entries-${c.slug}`}>
                <Eye className="h-4 w-4" /> Participations
              </button>
              <ChallengeForm styles={styles} challenge={c} onSaved={load} />
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!entriesFor} onOpenChange={(o) => !o && setEntriesFor(null)}>
        <DialogContent className="glass max-w-lg">
          <DialogHeader><DialogTitle>Participations — {entriesFor?.title}</DialogTitle></DialogHeader>
          <div className="max-h-96 space-y-2 overflow-y-auto">
            {entries.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Aucune participation.</p>}
            {entries.map((e) => (
              <div key={e.id} className="flex items-center justify-between rounded-xl border border-border bg-surface-card p-3">
                <div>
                  <p className="font-medium text-white">{e.song_title}</p>
                  <p className="text-xs text-muted-foreground">{e.creator?.display_name} · {e.status}</p>
                </div>
                {e.status === 'active' && (
                  <button onClick={() => removeEntry(e.id)} className="text-destructive hover:text-destructive/80" data-testid={`remove-entry-${e.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChallengeForm({ styles, challenge, onSaved }) {
  const editing = !!challenge;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    title: challenge?.title || '', description: challenge?.description || '',
    emoji: challenge?.emoji || '🔥', style_id: challenge?.style_id || 'none',
    status: challenge?.status || 'active',
    start_at: challenge?.start_at?.slice(0, 16) || '', end_at: challenge?.end_at?.slice(0, 16) || '',
  });

  const submit = async () => {
    if (!f.title.trim()) { toast.error('Titre requis'); return; }
    const payload = { ...f, style_id: f.style_id === 'none' ? null : f.style_id,
      start_at: f.start_at || null, end_at: f.end_at || null };
    try {
      if (editing) await api.put(`/admin/challenges/${challenge.id}`, payload);
      else await api.post('/admin/challenges', payload);
      toast.success(editing ? 'Challenge modifié' : 'Challenge créé');
      setOpen(false); onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className={editing
          ? 'rounded-full bg-surface-cardHover px-4 py-2 text-sm text-white hover:bg-white/10'
          : 'flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-black btn-glow'}
          data-testid={editing ? `admin-edit-${challenge.slug}` : 'admin-create-challenge'}>
          {editing ? 'Modifier' : <><Plus className="h-4 w-4" /> Nouveau challenge</>}
        </button>
      </DialogTrigger>
      <DialogContent className="glass max-w-md">
        <DialogHeader><DialogTitle>{editing ? 'Modifier' : 'Créer'} un challenge</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Titre" className={inp} data-testid="challenge-title" />
          <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Description" rows={2} className={`${inp} resize-none`} data-testid="challenge-description" />
          <div className="flex gap-3">
            <input value={f.emoji} onChange={(e) => setF({ ...f, emoji: e.target.value })} placeholder="Emoji" className={`${inp} w-24`} />
            <Select value={f.style_id} onValueChange={(v) => setF({ ...f, style_id: v })}>
              <SelectTrigger className="flex-1 border-border bg-surface-input"><SelectValue placeholder="Style" /></SelectTrigger>
              <SelectContent className="glass">
                <SelectItem value="none">Tous styles</SelectItem>
                {styles.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-3">
            <label className="flex-1 text-xs text-muted-foreground">Début<input type="datetime-local" value={f.start_at} onChange={(e) => setF({ ...f, start_at: e.target.value })} className={inp} /></label>
            <label className="flex-1 text-xs text-muted-foreground">Fin<input type="datetime-local" value={f.end_at} onChange={(e) => setF({ ...f, end_at: e.target.value })} className={inp} /></label>
          </div>
          <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
            <SelectTrigger className="border-border bg-surface-input"><SelectValue /></SelectTrigger>
            <SelectContent className="glass">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <button onClick={submit} className="w-full rounded-full bg-brand py-2.5 font-semibold text-black btn-glow" data-testid="challenge-submit">
            {editing ? 'Enregistrer' : 'Créer'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
